<template>
  <div
    ref="trigger"
    :class="{ 'status-bubble': true, [normalizedStatus]: true, 'is-open': isOpen }"
    @click.stop="openPopup"
    v-click-outside="closePopup"
  >
    {{ statusText }}
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="popup"
        :class="{ 'status-popup-chooser': true, [normalizedStatus]: true }"
        :style="popupStyle"
        @click.stop
      >
        <div class="status-popup-chooser-item" v-for="option in options" :key="option.value">
          <img
            :style="{ opacity: normalizedStatus === option.value ? '1' : '0' }"
            src="@/assets/checkmark.svg"
          />
          <div
            class="bubble"
            :class="option.value"
            :data-status="option.value"
            @click.stop="selectStatus(option.value)"
          >
            {{ option.text }}
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import useQuoteMutate from '@/composables/useQuoteMutate';
import useAlerts from '@/composables/useAlerts';

const STATUS_TOP_OFFSETS_REM = {
  open: -0.7,
  rejected: -2.9,
  converted: -5.1,
};

export default {
  name: 'QuoteStatusSelector',
  props: {
    quote: {
      type: Object,
      required: true,
    },
  },
  emits: ['statusUpdated'],
  setup() {
    const { updateQuote } = useQuoteMutate();
    const { addAlert } = useAlerts();

    return { updateQuote, addAlert };
  },
  data() {
    return {
      isOpen: false,
      popupStyle: {},
      options: [
        { text: 'Open', value: 'open' },
        { text: 'Rejected', value: 'rejected' },
        { text: 'Converted', value: 'converted' },
      ],
    };
  },
  computed: {
    normalizedStatus() {
      return (this.quote.STATUS || 'open').toLowerCase();
    },
    statusText() {
      const option = this.options.find((option) => option.value === this.normalizedStatus);
      return option?.text || 'Open';
    },
  },
  beforeUnmount() {
    this.removePositionListeners();
  },
  methods: {
    openPopup() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.$nextTick(() => {
        this.updatePopupPosition();
        this.addPositionListeners();
      });
    },
    closePopup() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.popupStyle = {};
      this.removePositionListeners();
    },
    addPositionListeners() {
      window.addEventListener('scroll', this.closePopup, true);
      window.addEventListener('resize', this.closePopup);
    },
    removePositionListeners() {
      window.removeEventListener('scroll', this.closePopup, true);
      window.removeEventListener('resize', this.closePopup);
    },
    updatePopupPosition() {
      const trigger = this.$refs.trigger;
      const popup = this.$refs.popup;
      if (!trigger || !popup) return;

      const triggerRect = trigger.getBoundingClientRect();
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const leftOffset = -2.68 * rem;

      // Rough place first so the selected option is measurable
      const topOffset = (STATUS_TOP_OFFSETS_REM[this.normalizedStatus] ?? -0.7) * rem;
      this.popupStyle = {
        position: 'fixed',
        top: `${triggerRect.top + topOffset}px`,
        left: `${triggerRect.left + leftOffset}px`,
        zIndex: 2000,
      };

      this.$nextTick(() => {
        const selectedBubble = popup.querySelector(`[data-status="${this.normalizedStatus}"]`);
        if (!selectedBubble) return;

        const selectedRect = selectedBubble.getBoundingClientRect();
        const top = parseFloat(this.popupStyle.top) - (selectedRect.top - triggerRect.top);
        const left = parseFloat(this.popupStyle.left) - (selectedRect.left - triggerRect.left);

        this.popupStyle = {
          position: 'fixed',
          top: `${top}px`,
          left: `${left}px`,
          zIndex: 2000,
        };
      });
    },
    async selectStatus(status) {
      this.closePopup();
      if (status === this.normalizedStatus) return;

      const previousStatus = this.quote.STATUS;
      this.quote.STATUS = status;
      const response = await this.updateQuote(this.quote.QUOTEID, { STATUS: status });

      if (!response?.updateQuote) {
        this.quote.STATUS = previousStatus;
        this.addAlert('Error updating quote status', 'high', 4);
        return;
      }

      this.$emit('statusUpdated', this.quote);
    },
  },
};
</script>

<style lang="scss" scoped>
.status-bubble {
  padding: 0.3rem 0.75rem;
  border-radius: 1rem;
  font-size: $fontSize12;
  font-weight: 400;
  text-wrap: nowrap;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  overflow: visible;
  cursor: pointer;
  width: fit-content;
  box-sizing: border-box;

  &.open {
    background: #d49a00;
    color: white;
  }

  &.rejected {
    background: #bf262f;
    color: white;
  }

  &.converted {
    background: #3eb35a;
    color: white;
  }
}
</style>

<style lang="scss">
.status-popup-chooser {
  position: fixed;
  box-shadow: $shadow1;
  z-index: 2000;
  padding: 0.75rem;
  background: white;
  overflow: visible;
  border-radius: $borderRadius4;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  justify-content: flex-start;
  cursor: default;

  .status-popup-chooser-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 0.5rem;

    img {
      width: 1.1rem;
      height: 1.1rem;
    }

    .bubble {
      padding: 0.3rem 0.75rem;
      border-radius: 1rem;
      font-size: $fontSize12;
      font-weight: 400;
      text-wrap: nowrap;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      cursor: pointer;
      width: fit-content;
      box-sizing: border-box;

      &.open {
        background: #d49a00;
        color: white;
      }

      &.rejected {
        background: #bf262f;
        color: white;
      }

      &.converted {
        background: #3eb35a;
        color: white;
      }
    }
  }
}
</style>
